import React from "react";
import { formatCurrency } from "../utils/formatCurrency";

const CHART_HEIGHT = 150;

const BarChart = ({ scenarios, cmTarget }) => {
  const maxVal = Math.max(
    ...scenarios.map((s) => Math.abs(s.showProfit)),
    cmTarget * 1.1
  );
  const scale = (val) => Math.max(4, (Math.abs(val) / maxVal) * CHART_HEIGHT);
  const targetY = (cmTarget / maxVal) * CHART_HEIGHT;

  return (
    <div>
      <div className="small text-muted mb-3">
        CM Target: {formatCurrency(cmTarget)} per show
      </div>
      <div style={{ position: "relative", height: CHART_HEIGHT + 40 }}>
        {/* CM target line */}
        <div
          style={{
            position: "absolute",
            bottom: targetY + 20,
            left: 0,
            right: 0,
            borderTop: "2px dashed #1982C4",
            zIndex: 2,
          }}
        >
          <span
            style={{
              position: "absolute",
              right: 0,
              top: -18,
              color: "#1982C4",
              fontWeight: 600,
              fontSize: "0.7rem",
            }}
          >
            CM Target
          </span>
        </div>

        {/* Bars */}
        <div
          className="d-flex justify-content-around"
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: CHART_HEIGHT + 40,
            alignItems: "flex-end",
          }}
        >
          {scenarios.map((s) => {
            const barH = scale(s.showProfit);
            const barColor = s.hitsCM
              ? "#28a745"
              : s.showProfit > 0
              ? "#ffc107"
              : "#FF595E";

            return (
              <div
                key={s.label}
                className="text-center"
                style={{ flex: "1 1 0", padding: "0 4px" }}
              >
                <div
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: s.isExpected ? 700 : 400,
                    color: barColor,
                    marginBottom: 4,
                  }}
                >
                  {formatCurrency(s.showProfit)}
                </div>
                <div
                  style={{
                    height: barH,
                    backgroundColor: barColor,
                    borderRadius: "4px 4px 0 0",
                    opacity: s.isExpected ? 1 : 0.6,
                    border: s.isExpected
                      ? "2px solid rgba(0,0,0,0.15)"
                      : "none",
                    transition: "height 0.3s ease",
                  }}
                />
                <div
                  style={{
                    fontSize: "0.7rem",
                    color: "var(--od-c-888888, #888)",
                    marginTop: 4,
                  }}
                >
                  {s.attendance}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const ScenarioTable = ({ scenarios, venue, cmTarget }) => {
  const rows = [
    { key: "attendance", label: "Attendance", format: (v) => v.toLocaleString() },
    { key: "ticketGross", label: "Ticket Gross", format: formatCurrency },
    { key: "addOnRevenue", label: "Add-On Revenue", format: formatCurrency },
    { key: "fbRevenue", label: "F&B Revenue", format: formatCurrency },
    { key: "totalRevenue", label: "Total Revenue", format: formatCurrency, bold: true, className: "border-top" },
    { key: "divider1", isDivider: true },
    { key: "artistPayout", label: "Artist Payout", format: formatCurrency, negative: true },
    { key: "totalExpenses", label: "Show Expenses", format: formatCurrency, negative: true },
    { key: "venueRoyalty", label: "Venue Royalty", format: formatCurrency, negative: true },
    { key: "divider2", isDivider: true },
    { key: "showProfit", label: "Show Profit", format: formatCurrency, bold: true, colored: true },
    { key: "cmDelta", label: `vs. CM Target (${formatCurrency(cmTarget)})`, format: formatCurrency, colored: true },
  ];

  return (
    <div className="card" style={{ borderRadius: 8, border: "1px solid var(--od-c-e9eaeb, #e9eaeb)" }}>
      <div className="card-body">
        <h6 style={{ fontWeight: 600 }} className="mb-3">
          Scenario Analysis
        </h6>
        <div className="table-responsive">
          <table className="table table-sm mb-0">
            <thead>
              <tr>
                <th
                  className="border-0 text-muted small"
                  style={{ width: "30%" }}
                ></th>
                {scenarios.map((s) => (
                  <th
                    key={s.label}
                    className="border-0 text-center small"
                    style={{
                      fontWeight: s.isExpected ? 700 : 400,
                      color: s.isExpected ? "var(--od-c-333333, #333)" : "var(--od-c-888888, #888)",
                      backgroundColor: s.isExpected
                        ? "rgba(25,130,196,0.05)"
                        : "transparent",
                      borderRadius: s.isExpected ? "8px 8px 0 0" : 0,
                    }}
                  >
                    {s.isExpected && (
                      <span
                        className="badge mb-1 d-block"
                        style={{
                          backgroundColor: "#1982C4",
                          color: "#fff",
                          fontSize: "0.7rem",
                        }}
                      >
                        YOUR FORECAST
                      </span>
                    )}
                    {s.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                if (row.isDivider) {
                  return (
                    <tr key={row.key}>
                      <td
                        colSpan={5}
                        style={{
                          height: 1,
                          padding: 0,
                          borderTop: "2px solid var(--od-c-dee2e6, #dee2e6)",
                        }}
                      ></td>
                    </tr>
                  );
                }

                return (
                  <tr key={row.key} className={row.className || ""}>
                    <td
                      className="small"
                      style={{
                        fontWeight: row.bold ? 600 : 400,
                        paddingTop: 8,
                        paddingBottom: 8,
                      }}
                    >
                      {row.negative && (
                        <span className="text-muted mr-1">&minus;</span>
                      )}
                      {row.label}
                    </td>
                    {scenarios.map((s) => {
                      const val = s[row.key];
                      let color = "var(--od-c-333333, #333)";
                      if (row.colored) {
                        if (row.key === "showProfit") {
                          color =
                            val >= cmTarget
                              ? "#28a745"
                              : val > 0
                              ? "#ffc107"
                              : "#FF595E";
                        } else {
                          color =
                            val >= 0 ? "#28a745" : "#FF595E";
                        }
                      }

                      return (
                        <td
                          key={s.label}
                          className="text-center"
                          style={{
                            fontWeight: row.bold || row.colored ? 600 : 400,
                            fontSize: row.bold ? "0.95rem" : "0.85rem",
                            color,
                            backgroundColor: s.isExpected
                              ? "rgba(25,130,196,0.05)"
                              : "transparent",
                            paddingTop: 8,
                            paddingBottom: 8,
                          }}
                        >
                          {row.negative ? `(${row.format(val)})` : row.format(val)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Visual CM indicator */}
        <div className="mt-4 pt-3" style={{ borderTop: "1px solid var(--od-c-dee2e6, #dee2e6)" }}>
          <BarChart scenarios={scenarios} cmTarget={cmTarget} />
        </div>
      </div>
    </div>
  );
};

export default ScenarioTable;
